import { formatAprValue } from "rujira.ui";
import clsx from "clsx";
import { FC, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import {
  PreloadedQuery,
  useLazyLoadQuery,
  usePaginationFragment,
  usePreloadedQuery,
  useQueryLoader,
} from "react-relay";
import {
  AssetLabel,
  Button,
  classAprValue,
  clipFloat,
  Decimal,
  Fiat,
  IconDenom,
  Icons,
  Slider,
  SortDir,
  SortableTableHeader,
  TableFrame,
  TablePagination,
  TableTooltipLabel,
  Toggle,
  useTablePagination,
  useTableSort,
  useTranslation,
} from "rujira.ui";
import { AnalyticsBoundary } from "../analytics/AnalyticsBoundary";
import { AnalyticsWarning } from "../analytics/AnalyticsWarning";
import { formatBigintPercent } from "../common/bigint";
import { SortBar } from "../common/components/SortBar";
import { StickyBar } from "../common/components/StickyBar";
import { useIsMobile } from "../hooks/useBreakpoint";
import { useListTopScroll } from "../hooks/useListTopScroll";
import { assetPairToTradeUrlKey } from "../services/assetUrl";
import { retryAnalyticsConnection } from "../services/relayAnalytics";
import {
  pairsQuery,
  query,
  rangesFragment,
} from "./analytics/AlgorithmicLeaderboard";
import { AlgorithmicLeaderboardPaginationQuery } from "./analytics/__generated__/AlgorithmicLeaderboardPaginationQuery.graphql";
import { AlgorithmicLeaderboardPairsQuery } from "./analytics/__generated__/AlgorithmicLeaderboardPairsQuery.graphql";
import { AlgorithmicLeaderboard_query$key } from "./analytics/__generated__/AlgorithmicLeaderboard_query.graphql";
import {
  AlgorithmicLeaderboardQuery,
  FinRangeStatus,
} from "./analytics/__generated__/AlgorithmicLeaderboardQuery.graphql";
import {
  LeaderboardRows,
  LeaderboardRowsFallback,
} from "./components/mobile/LeaderboardRows";
import { ALL_PAIRS, PairOption, PairSelect } from "./components/PairSelect";
import { RangeActions } from "./components/RangeActions";
import { RangeCell } from "./components/RangeCell";
import {
  FRACTION,
  MAX_PLAUSIBLE_AGE_DAYS,
  MAX_PLAUSIBLE_APR,
  MIN_AGE_DAYS,
  MIN_PLAUSIBLE_APR,
  MIN_VALUE_USD,
  USD,
} from "./constants";
import { formatAge, formatMultiple, pct } from "./leaderboard/format";
import {
  FilterDim,
  RangeNode,
  Ranges,
  Row,
  SortKey,
} from "./leaderboard/types";

const DISPLAY_LIMIT = 50;
// Larger pages exceed the analytics API's rate limit.
const PAGE_SIZE = 50;
// Bound automatic backfill to five sequential requests.
const MAX_POOL = PAGE_SIZE * 5;
const BACKFILL_DEBOUNCE_MS = 400;

type PairsNode = NonNullable<
  NonNullable<
    NonNullable<
      AlgorithmicLeaderboardPairsQuery["response"]["finV3"]["pairs"]
    >["edges"]
  >[number]
>["node"];

type LeaderboardPairAsset = NonNullable<PairsNode>["assetBase"];

type LeaderboardPairOption = PairOption<LeaderboardPairAsset> & {
  contract: string;
};

const PRICE = 1e12; // high / low / price + quote USD price
const MULTIPLE = 1e12; // moic / dpi

// valueUsd(1e8) / quotePrice(1e12) * 10^quoteDecimals
const valueInQuote = (
  valueUsd: bigint,
  quotePriceUsd: bigint,
  quoteDecimals: number
): bigint =>
  quotePriceUsd > 0n
    ? (valueUsd * 10n ** BigInt(quoteDecimals + 4)) / quotePriceUsd
    : 0n;

const toRow = (node: RangeNode): Row => {
  const a = node.analytics;
  const quote = node.pair.assetQuote;
  const quotePrice = BigInt(quote.price?.current ?? "0");
  const quoteDecimals = quote.metadata.decimals;
  const rawAge = a?.weightedAverageInvestmentAgeDays ?? 0;
  const ageValid = rawAge <= MAX_PLAUSIBLE_AGE_DAYS;
  return {
    node,
    baseSymbol: node.pair.assetBase.metadata.symbol,
    quoteSymbol: quote.metadata.symbol,
    pairKey: assetPairToTradeUrlKey(node.pair.assetBase, quote),
    valueQuote: valueInQuote(BigInt(node.valueUsd), quotePrice, quoteDecimals),
    ageValid,
    nums: {
      age: ageValid ? rawAge : 0,
      value: Number(node.valueUsd) / USD,
      apr: Math.min(
        MAX_PLAUSIBLE_APR,
        Math.max(MIN_PLAUSIBLE_APR, Number(a?.apr ?? 0n) / FRACTION)
      ),
      moic: Number(a?.moic ?? 0n) / MULTIPLE,
      dpi: Number(a?.dpi ?? 0n) / MULTIPLE,
      high: Number(node.high) / PRICE,
      low: Number(node.low) / PRICE,
      spread: Number(node.spread) / FRACTION,
      fee:
        node.rewardStrategy == null
          ? null
          : Number(node.rewardStrategy) / FRACTION,
      tightness:
        node.tightnessFactor == null
          ? null
          : Number(node.tightnessFactor) / FRACTION,
    },
  };
};

interface ColumnDef {
  key: SortKey;
  label: string;
  align?: "right" | "center";
  param?: boolean;
  tooltip?: string;
  group?: "start" | "end";
}

const COLUMNS: ColumnDef[] = [
  { key: "pair", label: "Pair" },
  {
    key: "age",
    label: "Age",
    tooltip:
      '<div class="w-30">Weighted average age of your position based on the size and timing of deposits and withdrawals. Used to calculate annualized returns like APR.<br/><br/>Example: a larger deposit made 30 days ago will have more impact on the calculation than a smaller deposit made yesterday.</div>',
  },
  {
    key: "moic",
    label: "MOIC",
    group: "start",
    tooltip:
      '<div class="w-30">Multiple of Invested Capital (MOIC) measures the total value of your position compared to the amount invested, including both realized and unrealized value.<br/><br/>Example: if you invested $1,000, already received $500 back from the position, and still have $1,500 remaining in the position, your total value is $2,000 and your MOIC is 2.0x.<br/><br/>Calculated as: (Realized Value + Unrealized Value) / Total Invested.</div>',
  },
  {
    key: "dpi",
    label: "DPI",
    align: "center",
    tooltip:
      '<div class="w-30">Distribution-to-Paid-In (DPI) ratio measures how much value has been returned compared to the total amount invested.<br/><br/>Example: if you invested $1,000 and received $1,500 back from the position, your DPI is 1.5x and your realized profit is $500.</div>',
  },
  {
    key: "apr",
    label: "APR",
    align: "right",
    group: "end",
    tooltip:
      '<div class="w-30">Estimated yearly return based on the claimable yield earned relative to the weighted average value and duration of your position. To measure APR, part of your yield must be set as claimable instead of fully auto-compounded.<br/><br/>Example: if your position earned $100 in claimable yield on an average invested value of $1,000 over one year, the APR would be 10%.</div>',
  },
  { key: "value", label: "Current Value", align: "right" },
  {
    key: "range",
    label: "Range",
    align: "center",
    param: true,
    group: "start",
  },
  {
    key: "tightness",
    label: "Tightness",
    align: "right",
    param: true,
    tooltip:
      '<div class="w-30">Range tightness measures how concentrated your liquidity is within your selected price range.<br/><br/>Lower values create a wider range that stays active across larger price movements, while higher values create a tighter range focused closer to the current market price. Tighter ranges can generate higher fees while the market stays within the range, but are also more likely to move out of range where no fees are earned.<br/><br/>Calculated as: Tightness = Low Price / High Price.</div>',
  },
  { key: "spread", label: "Spread", align: "right", param: true },
  {
    key: "fee",
    label: "Reward Strategy",
    align: "right",
    param: true,
    group: "end",
    tooltip:
      '<div class="w-30">Determines how trading fee profits are distributed. Lower values reinvest more rewards back into your position, while higher values increase the amount of claimable yield.<br/><br/>Example: a 0% setting fully auto-compounds rewards into the position, while a 100% setting makes all rewards claimable instead of reinvested.<br/><br/>Choosing 0% fully auto-compounds rewards, which means APR tracking will not be available.</div>',
  },
];

const LEADERBOARD_TABLE_COLUMNS = [
  { id: "rank", width: "1.75rem" },
  { id: "logo", width: "3rem" },
  { id: "pair", width: "8rem" },
  { id: "age", width: "2rem" },
  { id: "moic", width: "2.875rem" },
  { id: "dpi", width: "2.875rem" },
  { id: "apr", width: "3.375rem" },
  { id: "value", width: "7.5rem" },
  { id: "range", width: "12.625rem" },
  { id: "tightness", width: "6rem" },
  { id: "spread", width: "5rem" },
  { id: "fee", width: "7rem" },
  { id: "actions", width: "8rem" },
] as const;

const leaderboardMinimumWidth = LEADERBOARD_TABLE_COLUMNS.reduce(
  (total, column) => total + parseFloat(column.width),
  0
);
// Percentage math on table columns can resolve to auto. Container units keep
// these widths as lengths, so each requested space transfer is respected.
const LEADERBOARD_LAYOUT_COLUMNS = LEADERBOARD_TABLE_COLUMNS.map((column) => ({
  ...column,
  minWidth: column.width,
  width:
    column.id === "rank" || column.id === "logo" || column.id === "actions"
      ? column.width
      : `calc(${column.width} + max(0rem, 100cqw - ${leaderboardMinimumWidth}rem) / ${COLUMNS.length})`,
}));

const defaultSortDir = (key: SortKey) =>
  key === "pair" ? SortDir.Asc : SortDir.Desc;

// Range does not have a useful compact-card sort on mobile.
const LEADERBOARD_SORTS: ColumnDef[] = COLUMNS.filter(
  (column) => column.key !== "range"
);

interface FilterDef {
  key: FilterDim;
  label: string;
  format: (v: number) => string;
  skewLow?: boolean;
}

// Biases slider precision toward smaller values and still works when min is 0.
const SKEW_EXPONENT = 2;
const toSkewedPos = (value: number, min: number, max: number): number => {
  const t = max > min ? (value - min) / (max - min) : 0;
  return Math.max(0, Math.min(1, t)) ** (1 / SKEW_EXPONENT);
};
const fromSkewedPos = (pos: number, min: number, max: number): number =>
  min + (max - min) * pos ** SKEW_EXPONENT;

// react-slider may return reversed or out-of-bounds thumbs when they overlap.
const clampRange = (
  val: number | readonly number[],
  min: number,
  max: number
): Ranges => {
  const [a, b] = val as number[];
  const lo = Math.min(Math.max(a, min), max);
  const hi = Math.min(Math.max(b, min), max);
  return lo <= hi ? [lo, hi] : [hi, lo];
};

const FILTER_GROUPS: { label: string; filters: FilterDef[] }[] = [
  {
    label: "General",
    filters: [
      { key: "age", label: "Age", format: formatAge, skewLow: true },
      {
        key: "value",
        label: "Current Value",
        format: (v) => `$${clipFloat(v)}`,
        skewLow: true,
      },
    ],
  },
  {
    label: "Performance",
    filters: [
      { key: "apr", label: "APR", format: pct },
      { key: "moic", label: "MOIC", format: formatMultiple },
      { key: "dpi", label: "DPI", format: formatMultiple },
    ],
  },
  {
    label: "Parameters",
    filters: [
      { key: "spread", label: "Spread", format: pct },
      { key: "fee", label: "Reward Strategy", format: pct },
      { key: "tightness", label: "Tightness", format: pct },
    ],
  },
];

const FILTERS: FilterDef[] = FILTER_GROUPS.flatMap((g) => g.filters);

// Closed positions have no current value, so minimums apply only to open ones.
const defaultFilters = (
  domains: Record<FilterDim, Ranges>,
  status: FinRangeStatus
): Record<FilterDim, Ranges> => {
  const f = {} as Record<FilterDim, Ranges>;
  const applyFloors = status === "OPEN";
  FILTERS.forEach(({ key }) => {
    const [min, max] = domains[key];
    const floor =
      applyFloors && key === "age"
        ? Math.min(Math.max(min, MIN_AGE_DAYS), max)
        : applyFloors && key === "value"
          ? Math.min(Math.max(min, MIN_VALUE_USD), max)
          : min;
    f[key] = [floor, max];
  });
  return f;
};

const FilterBarFallback: FC<{ isMobile: boolean }> = ({ isMobile }) => (
  <StickyBar
    ringed
    sticky={false}
    className="algorithmic-leaderboard__bar card px-2 px-md-3 py-2 flex ai-c wrap gap-x-1 gap-y-2 gap-sm-2">
    <div className="skeleton h-3 h-sm-5 w-9 br-2" />
    <div className="skeleton h-1.5 h-sm-2 w-5 w-sm-8 br-2" />
    <div className="skeleton h-3 w-15 br-2 ml-a" />
    {isMobile && <div className="skeleton h-5 w-full br-2" />}
  </StickyBar>
);

export const LeaderboardFallback: FC = () => {
  const isMobile = useIsMobile();

  if (isMobile)
    return (
      <>
        <FilterBarFallback isMobile />
        <LeaderboardRowsFallback />
      </>
    );

  return (
    <>
      <FilterBarFallback isMobile={false} />
      <div className="card p-3">
        <TableFrame
          className="algorithmic-leaderboard__table"
          columns={LEADERBOARD_LAYOUT_COLUMNS}
          tableClassName="table table--fixed table--big condensed">
          <thead>
            <tr>
              <th
                className="algorithmic-leaderboard__rank-col"
                aria-label="Rank"
              />
              <th
                className="algorithmic-leaderboard__logo-col"
                aria-label="Token logos"
              />
              {COLUMNS.map((c) => (
                <th key={c.key} className={clsx({ "bg-grey-1": c.param })}>
                  {c.label}
                </th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {[...Array(8)].map((_, i) => (
              <tr key={`algo-skeleton-${i}`}>
                {[...Array(COLUMNS.length + 3)].map((__, j) => (
                  <td
                    key={j}
                    className={clsx({
                      "algorithmic-leaderboard__rank-col": j === 0,
                      "algorithmic-leaderboard__logo-col": j === 1,
                    })}>
                    <div className="skeleton h-1.5 w-6 br-2" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </TableFrame>
      </div>
    </>
  );
};

const LeaderboardAnalyticsFallback: FC<{ onRetry: () => void }> = ({
  onRetry,
}) => (
  <div className="card p-3 flex ai-c jc-c gap-2">
    <AnalyticsWarning onRetry={onRetry} />
  </div>
);

const LeaderboardInner: FC<{
  pair?: string;
  onPairChange?: (pair: string) => void;
}> = ({ pair: selectedPair, onPairChange }) => {
  const [status, setStatus] = useState<FinRangeStatus>("OPEN");
  const [showFilters, setShowFilters] = useState(false);
  // Resolve pair contracts before loading the more expensive ranges query.
  const allPairsData = useLazyLoadQuery<AlgorithmicLeaderboardPairsQuery>(
    pairsQuery,
    {}
  );
  const pairOptions = useMemo<LeaderboardPairOption[]>(() => {
    const edges = allPairsData.finV3.pairs?.edges ?? [];
    const options = edges
      .map((e) => e?.node)
      .filter((n): n is NonNullable<typeof n> => !!n)
      .map((n) => ({
        key: assetPairToTradeUrlKey(n.assetBase, n.assetQuote),
        asset: { baseAsset: n.assetBase, quoteAsset: n.assetQuote },
        contract: n.address,
      }));
    const label = (o: LeaderboardPairOption) =>
      `${o.asset.baseAsset.metadata.symbol}/${o.asset.quoteAsset.metadata.symbol}`;
    return options.sort((a, b) => label(a).localeCompare(label(b)));
  }, [allPairsData]);
  const [pair, setPairState] = useState<string>(selectedPair ?? ALL_PAIRS);
  const setPair = useCallback(
    (nextPair: string) => {
      setPairState(nextPair);
      onPairChange?.(nextPair);
    },
    [onPairChange]
  );
  useEffect(() => {
    if (selectedPair === undefined) return;
    setPairState(selectedPair);
  }, [selectedPair]);
  const contracts = useMemo(
    () =>
      pair === ALL_PAIRS
        ? null
        : [pairOptions.find((option) => option.key === pair)?.contract].filter(
            (contract): contract is string => !!contract
          ),
    [pair, pairOptions]
  );
  const [q, load] = useQueryLoader<AlgorithmicLeaderboardQuery>(query);

  useEffect(() => {
    load({ status, count: PAGE_SIZE, contracts });
  }, [status, contracts, load]);

  if (!q) return <LeaderboardFallback />;
  return (
    <Suspense fallback={<LeaderboardFallback />}>
      <Content
        key={`${status}:${pair}`}
        q={q}
        status={status}
        setStatus={setStatus}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        pairOptions={pairOptions}
        pair={pair}
        setPair={setPair}
      />
    </Suspense>
  );
};

export const Leaderboard: FC<{
  pair?: string;
  onPairChange?: (pair: string) => void;
}> = ({ pair, onPairChange }) => {
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => {
    retryAnalyticsConnection();
    setAttempt((a) => a + 1);
  }, []);

  return (
    <AnalyticsBoundary
      key={attempt}
      fallback={<LeaderboardAnalyticsFallback onRetry={retry} />}
      loading={<LeaderboardFallback />}>
      <LeaderboardInner pair={pair} onPairChange={onPairChange} />
    </AnalyticsBoundary>
  );
};

const Content: FC<{
  q: PreloadedQuery<AlgorithmicLeaderboardQuery>;
  status: FinRangeStatus;
  setStatus: (s: FinRangeStatus) => void;
  showFilters: boolean;
  setShowFilters: (fn: (s: boolean) => boolean) => void;
  pairOptions: LeaderboardPairOption[];
  pair: string;
  setPair: (p: string) => void;
}> = ({
  q,
  status,
  setStatus,
  showFilters,
  setShowFilters,
  pairOptions,
  pair,
  setPair,
}) => {
  const isMobile = useIsMobile();

  const queryData = usePreloadedQuery<AlgorithmicLeaderboardQuery>(query, q);
  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment<
    AlgorithmicLeaderboardPaginationQuery,
    AlgorithmicLeaderboard_query$key
  >(rangesFragment, queryData);

  const rows = useMemo(
    () =>
      (data.finV3.ranges?.edges ?? [])
        .map((e) => e?.node)
        .filter((n): n is RangeNode => !!n)
        .map(toRow),
    [data]
  );

  const domains = useMemo(() => {
    const d = {} as Record<FilterDim, Ranges>;
    FILTERS.forEach(({ key }) => {
      const vals = rows
        .map((r) => r.nums[key])
        .filter((value): value is number => value !== null);
      const min = vals.length ? Math.min(...vals) : 0;
      const max = vals.length ? Math.max(...vals) : 0;
      d[key] = [min, max === min ? min + 1 : max];
    });
    return d;
  }, [rows]);

  // Only user-touched sliders are stored; untouched dimensions fall back to
  // the live domain defaults. This matters once backfill pages arrive: a
  // captured [min, max] from the first page would silently exclude new rows
  // outside that range and the backfill would never converge.
  const [touched, setTouched] = useState<Partial<Record<FilterDim, Ranges>>>(
    {}
  );
  const filters = useMemo<Record<FilterDim, Ranges>>(
    () => ({ ...defaultFilters(domains, status), ...touched }),
    [domains, status, touched]
  );
  const {
    sortBy: sortKey,
    sortDir,
    toggleSort,
  } = useTableSort<SortKey>({
    initialBy: "apr",
    initialDir: SortDir.Desc,
    defaultDir: defaultSortDir,
  });

  const { anchor, scrollToList } = useListTopScroll(isMobile && !showFilters, [
    status,
    pair,
    sortKey,
    sortDir,
    touched,
  ]);

  const sortValue = (r: Row): number | string | null => {
    if (sortKey === "pair") return r.baseSymbol;
    if (sortKey === "range") return r.nums.low;
    return r.nums[sortKey];
  };

  const visible = useMemo(() => {
    const filtered = rows.filter((r) =>
      FILTERS.every(({ key }) => {
        const [lo, hi] = filters[key];
        const value = r.nums[key];
        return value !== null && value >= lo && value <= hi;
      })
    );
    filtered.sort((a, b) => {
      const av = sortValue(a);
      const bv = sortValue(b);
      if (av === null) return bv === null ? 0 : 1;
      if (bv === null) return -1;
      if (typeof av === "string" && typeof bv === "string") {
        return sortDir === SortDir.Asc
          ? av.localeCompare(bv)
          : bv.localeCompare(av);
      }
      const an = typeof av === "number" ? av : -Infinity;
      const bn = typeof bv === "number" ? bv : -Infinity;
      return sortDir === SortDir.Asc ? an - bn : bn - an;
    });
    return filtered.slice(0, DISPLAY_LIMIT);
  }, [rows, filters, sortKey, sortDir]);

  const backfilling =
    visible.length < DISPLAY_LIMIT && hasNext && rows.length < MAX_POOL;
  const { pageRows, pageSize, pagination } = useTablePagination(visible, {
    resetKey: `${status}:${pair}:${sortKey}:${sortDir}:${JSON.stringify(touched)}`,
  });

  useEffect(() => {
    if (!backfilling || isLoadingNext) return;
    const t = setTimeout(() => loadNext(PAGE_SIZE), BACKFILL_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [backfilling, isLoadingNext, loadNext, visible.length, rows.length]);

  return (
    <>
      <div
        ref={anchor}
        aria-hidden
        className="scroll-anchor scroll-anchor--in-gap"
      />
      <StickyBar
        ringed
        collapseLabel="Show Filters"
        onCollapse={() => setShowFilters(() => false)}
        expanded={showFilters}
        className="algorithmic-leaderboard__bar card px-2 px-md-3 py-2 flex ai-c wrap gap-x-1 gap-y-2 gap-sm-2">
        <Button
          className="algorithmic-leaderboard__filters button--sm button--outline button--grey"
          label={showFilters ? "Hide filters" : "Filters"}
          onClick={() => {
            const next = !showFilters;
            setShowFilters(() => next);
            // The panel wants the whole screen, so park the bar at the top
            // rather than leave the reader to scroll there themselves.
            if (next && isMobile) scrollToList(true);
          }}>
          <Icons.Sliders className="w-2 h-2 ml-0.5" />
        </Button>
        <button
          className="transparent flex ai-c gap-0.5 color-grey hover-white fs-10 fs-sm-12 fw-500 pointer"
          onClick={() => {
            setTouched({});
            setPair(ALL_PAIRS);
          }}>
          <Icons.Undo className="w-1.5 h-1.5 w-sm-2 h-sm-2" />
          <span>
            Reset<span className="show-sm-i"> filters</span>
          </span>
        </button>
        <Toggle
          className="algorithmic-leaderboard__status toggle--sm ml-a"
          labelOff="Closed"
          label="Open"
          checked={status === "OPEN"}
          onChange={(e) => setStatus(e.target.checked ? "OPEN" : "CLOSED")}
        />
        {isMobile && (
          <SortBar
            options={LEADERBOARD_SORTS}
            sortKey={sortKey}
            sortDir={sortDir}
            onKeyChange={toggleSort}
            onDirToggle={() => toggleSort(sortKey)}
          />
        )}
        {showFilters && (
          <FilterPanel
            domains={domains}
            filters={filters}
            setFilters={setTouched}
            pairOptions={pairOptions}
            pair={pair}
            setPair={setPair}
          />
        )}
      </StickyBar>

      {isMobile ? (
        <LeaderboardRows
          rows={visible}
          status={status}
          isLoadingNext={isLoadingNext}
          backfilling={backfilling}
        />
      ) : (
        <div className="card algorithmic-leaderboard px-3 pt-2 pb-3">
          <TableFrame
            className="algorithmic-leaderboard__table"
            columns={LEADERBOARD_LAYOUT_COLUMNS}
            tableClassName="table table--fixed nowrap table--spaced group-column-table condensed">
            <thead>
              <tr>
                <th
                  className="algorithmic-leaderboard__rank-col"
                  aria-hidden="true"
                />
                <th
                  className="algorithmic-leaderboard__logo-col"
                  aria-hidden="true"
                />
                <th colSpan={2} />
                <th colSpan={3} className="text-center nopad">
                  <div className="group-column-table__group-header bg-grey-1 text-center fs-12 color-grey fw-400">
                    Performance
                  </div>
                </th>
                <th />
                <th colSpan={4} className="text-center nopad">
                  <div className="group-column-table__group-header bg-grey-1 text-center fs-12 color-grey fw-400">
                    Parameters
                  </div>
                </th>
                <th style={{ boxShadow: "none" }} />
              </tr>
              <tr>
                <th
                  className="algorithmic-leaderboard__rank-col"
                  aria-label="Rank"
                />
                <th
                  className="algorithmic-leaderboard__logo-col"
                  aria-label="Token logos"
                />
                {COLUMNS.map((c) => (
                  <SortableTableHeader
                    key={c.key}
                    value={c.key}
                    active={sortKey}
                    dir={sortDir}
                    onSort={toggleSort}
                    align={c.align}
                    label={
                      c.tooltip ? (
                        <TableTooltipLabel
                          label={c.label}
                          tooltip={c.tooltip}
                        />
                      ) : (
                        c.label
                      )
                    }
                    contentClassName={clsx({
                      "color-white": sortKey === c.key,
                    })}
                    className={clsx({
                      "bg-grey-1": c.param || !!c.group || c.key === "dpi",
                      "group-column-table__group-start": c.group === "start",
                      "group-column-table__group-end": c.group === "end",
                      "algorithmic-leaderboard__pair-col": c.key === "pair",
                      "algorithmic-leaderboard__value-col": c.key === "value",
                      "algorithmic-leaderboard__age-col": c.key === "age",
                    })}
                  />
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row, i) => (
                <RangeRow
                  key={row.node.id}
                  row={row}
                  rank={pagination.pageIndex * pageSize + i + 1}
                  status={status}
                />
              ))}
              {isLoadingNext && (
                <tr>
                  <td
                    colSpan={COLUMNS.length + 3}
                    className="text-center color-grey p-3">
                    Searching more positions…
                  </td>
                </tr>
              )}
              {visible.length === 0 && !backfilling && (
                <tr>
                  <td
                    colSpan={COLUMNS.length + 3}
                    className="text-center color-grey p-3">
                    No positions match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </TableFrame>
          <TablePagination {...pagination} />
        </div>
      )}
    </>
  );
};

const FilterPanel: FC<{
  domains: Record<FilterDim, Ranges>;
  filters: Record<FilterDim, Ranges>;
  setFilters: (
    fn: (
      f: Partial<Record<FilterDim, Ranges>>
    ) => Partial<Record<FilterDim, Ranges>>
  ) => void;
  pairOptions: LeaderboardPairOption[];
  pair: string;
  setPair: (p: string) => void;
}> = ({ domains, filters, setFilters, pairOptions, pair, setPair }) => (
  <div className="algorithmic-leaderboard__filter-panel w-full flex dir-c gap-2 mb-1">
    {FILTER_GROUPS.map(({ label: groupLabel, filters: groupFilters }) => (
      <div key={groupLabel}>
        <h4 className="fs-14 condensed fw-400 mb-1">{groupLabel}</h4>
        <div className="row wrap gap-y-2 pad">
          {groupLabel === "General" && (
            <div className="col-12 col-md-6 col-lg-4">
              <PairSelect options={pairOptions} pair={pair} setPair={setPair} />
            </div>
          )}
          {groupFilters.map(({ key, label, format, skewLow }) => {
            const [min, max] = domains[key];
            const [lo, hi] = filters[key];
            const slider = skewLow
              ? {
                  min: 0,
                  max: 1,
                  step: 0.001,
                  value: [
                    toSkewedPos(lo, min, max),
                    toSkewedPos(hi, min, max),
                  ] as Ranges,
                  onChange: (val: number | readonly number[]) => {
                    const [p0, p1] = clampRange(val, 0, 1);
                    setFilters((f) => ({
                      ...f,
                      [key]: [
                        fromSkewedPos(p0, min, max),
                        fromSkewedPos(p1, min, max),
                      ] as Ranges,
                    }));
                  },
                }
              : {
                  min,
                  max,
                  step: (max - min) / 100 || 1,
                  value: [lo, hi] as Ranges,
                  onChange: (val: number | readonly number[]) =>
                    setFilters((f) => ({
                      ...f,
                      [key]: clampRange(val, min, max),
                    })),
                };
            return (
              <div key={key} className="col-12 col-md-6 col-lg-4">
                <div className="flex ai-c jc-sb mb-1">
                  <label className="fs-14 condensed color-grey fw-500">
                    {label}
                  </label>
                  <span className="fs-14 condensed color-white mono">
                    {format(lo)} – {format(hi)}
                  </span>
                </div>
                <Slider {...slider} minDistance={0} />
              </div>
            );
          })}
        </div>
      </div>
    ))}
  </div>
);

const RangeRow: FC<{ row: Row; rank: number; status: FinRangeStatus }> = ({
  row,
  rank,
  status,
}) => {
  const { t } = useTranslation("common");
  const { node, baseSymbol, quoteSymbol, valueQuote } = row;
  const { assetBase, assetQuote } = node.pair;
  const a = node.analytics;
  const noYield = BigInt(node.fee) === 0n;

  return (
    <tr>
      <td className="color-grey algorithmic-leaderboard__rank-col">#{rank}</td>

      <td className="algorithmic-leaderboard__logo-col">
        <div className="lp">
          <IconDenom denom={baseSymbol} />
          <IconDenom denom={quoteSymbol} />
        </div>
      </td>
      <td className="algorithmic-leaderboard__pair-col">
        <div className="condensed fw-500 nowrap flex fs-18 ai-b">
          <AssetLabel
            asset={assetBase}
            Container={({ children }) => (
              <small className="color-grey">{children}</small>
            )}
          />
          <div className="color-grey">
            /
            <AssetLabel
              asset={assetQuote}
              Container={({ children }) => (
                <small className="color-grey">{children}</small>
              )}
            />
          </div>
        </div>
      </td>

      <td className="nowrap algorithmic-leaderboard__age-col">
        {a && row.ageValid ? formatAge(row.nums.age) : "—"}
      </td>

      <td className="text-left mono group-column-table__group-start bg-grey-1">
        {formatMultiple(row.nums.moic)}
      </td>
      <td className="text-center mono bg-grey-1">
        {formatMultiple(row.nums.dpi)}
      </td>

      <td className="text-right mono group-column-table__group-end bg-grey-1">
        {noYield ? (
          <span
            className="color-teal iflex ai-c"
            tabIndex={0}
            aria-label={t("compounding")}
            data-tooltip-content="This position compounds automatically, with rewards reinvested"
            data-tooltip-id="global-tip">
            <Icons.Seedling className="w-2 h-2" aria-hidden="true" />
          </span>
        ) : (
          <span className={classAprValue(a?.apr)}>
            {formatAprValue(BigInt(a?.apr ?? 0n))}
          </span>
        )}
      </td>
      <td className="text-right algorithmic-leaderboard__value-col">
        <div className="flex dir-c ai-e w-full">
          <Decimal
            amount={valueQuote}
            symbol={quoteSymbol}
            decimals={assetQuote.metadata.decimals}
            className="fs-16 condensed fw-500"
            clip
          />
          <Fiat
            amount={BigInt(node.valueUsd)}
            symbol="$"
            decimals={8}
            className="color-grey fs-12 condensed"
          />
        </div>
      </td>

      <td className="mono group-column-table__group-start bg-grey-1">
        <RangeCell node={node} status={status} />
      </td>
      <td className="text-right mono bg-grey-1">
        {row.nums.tightness === null ? "—" : pct(row.nums.tightness)}
      </td>
      <td className="text-right mono bg-grey-1">
        {formatBigintPercent(BigInt(node.spread))}
      </td>
      <td className="text-right mono group-column-table__group-end bg-grey-1">
        {noYield || row.nums.fee === null ? (
          <span className="color-grey">&mdash;</span>
        ) : (
          pct(row.nums.fee)
        )}
      </td>

      <td>
        <RangeActions row={row} />
      </td>
    </tr>
  );
};
